/** As histórias de PNL, EQP e CMB (p.51 a p.53). A lista inteira e a ordem estão em `../catalogo.ts`. */
import type { Historia } from '../catalogo';

const REVISAO = '05/10/2026';

export const HISTORIAS: readonly Historia[] = [
  /* ------------------------------------------------ PNL · Painel (p.51) */
  {
    codigo: 'IND-PNL-01', titulo: 'Máquinas indisponíveis', papel: 'gestor operacional',
    quero: 'ver quantas máquinas não podem trabalhar agora', para: 'remanejar a frota antes que a obra pare',
    significa: 'Máquinas com status Parada ou Em manutenção no momento em que o painel abre. O clique abre a frota.',
    calcula: 'Soma das máquinas ativas cuja situação aberta é Parada ou Em manutenção, contada pela distribuição por status da frota inteira, e não pela lista das dez mais antigas. A barra mostra a parte da frota ativa.',
    origem: 'Equipamentos · situação aberta de cada máquina. Recalcula a cada abertura do painel; no app, também a cada 5 min.',
    criterios: [
      'Registrar Parada numa máquina soma 1 ao número na abertura seguinte.',
      'Com 14 máquinas paradas o card diz 14, e não 10.',
      'O toque abre a frota: Equipamentos na mesa, Frota no app.',
      'Sem permissão de ler equipamentos, o card diz "sem acesso à frota", e não zero.',
    ],
    situacao: 'em_revisao', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'IND-PNL-02', titulo: 'Preventivas vencidas', papel: 'planejador de manutenção',
    quero: 'ver as preventivas vencidas', para: 'tirar a máquina antes da quebra',
    significa: 'Preventivas cujo intervalo, em horas ou em dias, passou sem realização registrada.',
    calcula: 'Conta as tarefas do plano em estado vencida: o que falta até o vencimento, em h, km ou dias, chegou a zero ou passou. O contexto diz quantas vencem logo.',
    origem: 'Manutenção · vencimentos, pela última leitura do medidor e pela data de hoje. A conta é a mesma da tela de manutenção.',
    criterios: [
      'Registrar a realização da tarefa tira a preventiva da contagem.',
      'Número acima de zero sai em vermelho; zero sai em preto.',
      'O toque abre a Manutenção.',
    ],
    situacao: 'em_revisao', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },
  {
    codigo: 'IND-PNL-03', titulo: 'O.S. abertas', papel: 'oficina e mecânicos',
    quero: 'ver quantas O.S. estão abertas', para: 'saber o tamanho da fila antes de planejar o dia',
    significa: 'Ordens de serviço ainda não liberadas: em reparo, esperando peça ou em teste, esperando liberação.',
    calcula: 'Conta as O.S. nas etapas Solicitada, Triagem, Diagnóstico, Planejada, Aguardando peça, Em execução e Em teste. Liberada, Encerrada e Cancelada ficam fora. O contexto diz quantas aguardam peça.',
    origem: 'Ordens de serviço · etapa de cada O.S. Recalcula a cada abertura do painel.',
    criterios: [
      'Liberar a O.S. tira ela da contagem.',
      'O contexto "aguardando peça" bate com as O.S. nessa etapa na fila.',
      'O toque abre a fila de O.S.',
    ],
    situacao: 'em_revisao', responsavel: 'Oficina e mecânicos', revisadaEm: REVISAO,
  },
  {
    codigo: 'IND-PNL-04', titulo: 'Exceções de combustível', papel: 'gestor operacional',
    quero: 'ver o que falhou nas verificações do combustível', para: 'conferir desvio e perda antes do fechamento',
    significa: 'Lançamentos e pendências que falharam em alguma das quatro verificações do combustível: consumo fora da faixa, cobrança de posto divergente, tanque acima da capacidade e ajuste de tanque esperando aprovação.',
    calcula: 'Soma de quatro contagens: abastecimentos com consumo fora da faixa nos últimos 7 dias, cobranças divergentes, tanques com volume acima da capacidade e pedidos de ajuste aguardando aprovação. A mesa e o app usam a mesma conta.',
    origem: 'Combustível · consumo por abastecimento, cobranças dos postos, volume dos tanques e pedidos de ajuste. Recalcula a cada abertura do painel.',
    criterios: [
      'O contexto do card diz "em 4 verificações".',
      'O número é a soma das quatro linhas da seção Divergências de combustível, logo abaixo.',
      'Abastecimento sem leitura ou máquina sem faixa cadastrada não conta como exceção.',
      'ARLA não entra no consumo fora da faixa.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'IND-PNL-05', titulo: 'Custo por hora produtiva', papel: 'gestor operacional',
    quero: 'ver quanto custa cada hora em que a frota produziu', para: 'saber se a máquina parada ou ociosa está comendo a margem',
    significa: 'Custo direto do período dividido pelas horas em que as máquinas produziram.',
    calcula: 'Custo direto das máquinas com hora de produção apontada no período ÷ soma das horas de produção delas, em R$/h. Custo direto é combustível, peças, lubrificante e terceiros, sem rateio. O custo de máquina sem hora de produção fica fora da divisão e aparece escrito. A variação compara com o período anterior de mesma duração.',
    origem: 'Custos · custo direto por máquina; Operação · horas de produção dos apontamentos. Período em De e Até na mesa, 7, 30 ou 90 dias no app; sem escolha, os últimos 30 dias.',
    criterios: [
      'Sem hora de produção apontada, o card mostra "—" e "sem hora de produção apontada".',
      'Com máquina sem hora no período, o contexto diz o valor fora da conta, como "R$ 320,50 sem hora, fora da conta".',
      'O texto abaixo da faixa traz o custo por hora de motor, para comparação.',
      'O toque abre Custos.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'IND-PNL-06', titulo: 'Horas a explicar', papel: 'gestor operacional',
    quero: 'ver quantas horas ainda não batem com o cliente', para: 'resolver a divergência antes da medição',
    significa: 'Soma das diferenças de horas em aberto, sem compensar uma pela outra.',
    calcula: 'Soma do valor absoluto da diferença de cada divergência aberta, em h: 3 h a mais numa e 3 h a menos noutra dão 6 h, e não zero.',
    origem: 'Operação · divergências de apropriação abertas, apuradas acima da tolerância da empresa. Conta o que está aberto agora, e não o que aconteceu no período.',
    criterios: [
      'Resolver uma divergência tira as horas dela da soma.',
      'O contexto diz quantas divergências estão abertas com o cliente.',
      'O número bate com o card Horas a explicar da conciliação de horas.',
      'O toque abre a conciliação.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'COL-PNL-07', titulo: 'Pontos', papel: 'gestor operacional',
    quero: 'ver o peso de cada máquina na fila de prioridades', para: 'agir primeiro onde o problema é maior, e não onde ele grita mais',
    significa: 'Peso da máquina na fila de prioridades: preventiva vencida, O.S. aberta, divergência de horas, consumo fora da faixa, parada evitável, custo fora da curva.',
    calcula: 'Soma dos pesos: 3 por preventiva vencida, 3 por O.S. aberta com a máquina parada, 1 por O.S. aberta com a máquina em serviço, 2 por divergência de horas aberta, 1 por consumo fora da faixa no período, 1 a cada 8 h paradas por causa evitável e 2 quando o custo por hora passa de 1,5 vez a mediana da frota. A fila vai do maior para o menor; no empate, vem antes a que tem mais preventiva vencida.',
    origem: 'Visão do gestor · fila de prioridades, com os mesmos dados do painel e o custo direto do período. Sinal de módulo que o perfil não lê fica fora da conta, e a tela diz qual.',
    criterios: [
      'A regra da fila, abaixo da tabela, mostra os mesmos pesos.',
      'A coluna Por quê lista os sinais que somam os pontos da linha.',
      'Só entra na fila máquina com algum sinal.',
      'Abaixo de 1280 px a coluna sai da tabela, e a ordem da fila continua a mesma.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'NUM-PNL-08', titulo: 'Resultado', papel: 'controladoria',
    quero: 'ver o resultado de cada contrato no mês', para: 'saber se o contrato paga o custo total da frota que o serve',
    significa: 'Receita aprovada menos o custo total (direto, depreciação e indireto rateado) atribuído aos contratos da competência. "Resultado parcial" enquanto algo ficar fora do mês.',
    calcula: 'Receita das medições aprovadas da competência − custo total das máquinas do contrato no mês; a máquina de vários contratos entra na proporção das horas reconhecidas (ou apontadas) de cada um (0096). A margem é o resultado ÷ a receita. Resultado negativo sai em vermelho.',
    origem: 'Receita · medições aprovadas; Custos · custo direto por máquina; Equipamentos · alocações do mês. Mensal, pelo seletor de competência da seção, que não segue o período da faixa de cima.',
    criterios: [
      'Sem permissão de ler receita, a seção recusa e diz o que pedir, em vez de mostrar o custo como prejuízo.',
      'As premissas e o que não entrou aparecem antes da tabela.',
      'Nada nesta seção se chama lucro.',
      'Competência sem medição aprovada mostra o estado vazio, dizendo onde aprovar.',
    ],
    situacao: 'rascunho', responsavel: 'Controladoria', revisadaEm: REVISAO,
  },
  {
    codigo: 'NUM-PNL-09', titulo: 'Custo sem contrato', papel: 'controladoria',
    quero: 'ver o custo que ficou fora do resultado', para: 'não ler um resultado parcial como se fosse a frota inteira',
    significa: 'Custo que ficou fora do resultado por falta de alocação, ou de hora para dividir a máquina de vários contratos.',
    calcula: 'Soma do custo total das máquinas que, na competência, não estiveram alocadas a contrato nenhum, ou serviram mais de um sem hora reconhecida nem apontada para dividir (0096). Nenhuma é dividida igual por palpite: o custo fica neste balde, máquina por máquina. Máquina sem custo não entra.',
    origem: 'Custos · custo total por máquina; Equipamentos · alocações com contrato no mês. Mesma competência do Resultado.',
    criterios: [
      'Valor acima de zero sai como alerta; zero sai em preto.',
      'A lista abaixo da tabela diz cada máquina, o custo e se faltou alocação ou sobrou contrato.',
      'Alocar a máquina a um só contrato no mês tira o custo dela daqui e o leva para o contrato.',
    ],
    situacao: 'rascunho', responsavel: 'Controladoria', revisadaEm: REVISAO,
  },
  {
    codigo: 'NUM-PNL-10', titulo: 'Máquinas operando sem apontamento', papel: 'gestor operacional',
    quero: 'saber quais máquinas trabalham sem turno apontado', para: 'cobrar o apontamento antes que a hora se perca',
    significa: 'Máquinas com status Operando e sem apontamento de turno nos últimos dois dias. Aviso do Início, no app.',
    calcula: 'Máquinas ativas com situação Operando e nenhum turno apontado de anteontem para cá. A lista vem cortada em 10: com 10, o aviso diz "10 ou mais".',
    origem: 'Operação · apontamentos de turno; Equipamentos · situação da máquina. Recalcula com o painel, a cada 5 min no app.',
    criterios: [
      'Apontar o turno da máquina tira ela do aviso.',
      '"Ver" abre as Exceções, com a máquina listada em Operando sem apontamento.',
      'Sem máquina nessa condição, o aviso não aparece.',
    ],
    situacao: 'em_revisao', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },

  /* ------------------------------------------- EQP · Equipamentos (p.52) */
  {
    codigo: 'SEL-EQP-01', titulo: 'Status', papel: 'gestor operacional',
    quero: 'ver a situação de cada máquina', para: 'não alocar a que está na oficina',
    significa: 'Um dos seis status: Operando, Standby, Parada, Em manutenção, Disponível ou Em deslocamento. Não alocado ainda não é status (decisão do dono). Em manutenção quer dizer máquina na oficina; o selo muda quando alguém registra a nova situação.',
    calcula: 'A situação aberta da máquina, a última registrada. Máquina sem registro aparece como "Sem situação", que é a falta de registro e não um sétimo status. Parada e Em manutenção pedem a causa.',
    origem: 'Equipamentos · situação, registrada pela aba Situação da ficha. O painel aponta a máquina Em manutenção sem O.S. aberta.',
    criterios: [
      'O selo traz o nome escrito, nunca só a cor.',
      'Registrar uma nova situação troca o selo na lista e no app.',
      'Parada sem causa escolhida é recusada, dizendo o que falta.',
    ],
    situacao: 'em_revisao', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'COL-EQP-02', titulo: 'Horímetro', papel: 'planejador de manutenção',
    quero: 'ver o horímetro ou o odômetro atual da máquina', para: 'saber quanto falta para a próxima preventiva',
    significa: 'Última leitura do contador de horas do motor, com a data da leitura. Caminhão mostra o odômetro, em km.',
    calcula: 'A leitura mais recente da máquina, qualquer que seja a origem: abastecimento, turno, campo ou ajuste da ficha. Só o ajuste com motivo pode ser menor que a anterior.',
    origem: 'Operação · leituras de medidor. Aparece no prontuário da mesa (Horímetro atual), na ficha do app e na linha da Frota no app.',
    criterios: [
      'Uma leitura nova aparece na abertura seguinte da ficha.',
      'O prontuário mostra a data e a origem da leitura.',
      'Caminhão mostra km; máquina pesada mostra h.',
      'Sem leitura, a ficha diz "Sem leitura", e não zero.',
    ],
    situacao: 'em_revisao', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-EQP-03', titulo: 'Cadastrar equipamento', papel: 'administrador',
    quero: 'cadastrar uma máquina nova', para: 'ela existir para alocação, abastecimento e manutenção',
    significa: 'Abre a ficha em branco. O que não for preenchido vem da configuração do catálogo.',
    calcula: 'Grava a ficha com código e descrição obrigatórios. O servidor recusa código, série, série do motor, patrimônio, placa ou Renavam repetidos, ano fora de 1950 a 2100 e leitura inicial negativa, dizendo qual.',
    origem: 'Equipamentos · fichas das máquinas, com a herança da configuração do catálogo. Pede a permissão de gerir equipamento.',
    criterios: [
      'Código repetido é recusado com "Já existe uma máquina com esse código".',
      'O que ficou em branco aparece na ficha com a origem: herdado da configuração.',
      'A máquina nova aparece na lista da frota.',
    ],
    situacao: 'rascunho', responsavel: 'Administrador', revisadaEm: REVISAO,
  },
  {
    codigo: 'CMP-EQP-04', titulo: 'Configuração', papel: 'administrador',
    quero: 'escolher a versão exata do modelo', para: 'a ficha herdar a especificação sem eu digitar de novo',
    significa: 'A versão exata do modelo. Dela a ficha herda especificação e consumo esperado.',
    calcula: 'O que a ficha deixa em branco vem da configuração: medidor, capacidade do tanque, modelo do motor e faixa de consumo do manual. O valor da própria máquina vale sobre o herdado.',
    origem: 'Equipamentos · catálogo de fabricante, modelo e configuração. Opcional: sem ela, a ficha fica só com o que foi preenchido.',
    criterios: [
      'Cada valor herdado diz de onde veio, na ficha.',
      'A faixa de consumo da máquina vale sobre a da configuração.',
      'Sem configuração, a ficha mostra "Sem configuração".',
    ],
    situacao: 'rascunho', responsavel: 'Administrador', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-EQP-05', titulo: 'Registrar leitura', papel: 'operador',
    quero: 'anotar o horímetro sem fechar turno nem abastecer', para: 'a preventiva vencer na hora certa',
    significa: 'Lança uma leitura de horímetro ou odômetro, que nunca pode ser menor que a anterior. Na ficha da mesa, a correção com motivo é a única que pode.',
    calcula: 'No app, a leitura vai pela fila do aparelho e o servidor recusa a menor que a anterior, dizendo qual era. Na mesa, quem gere a máquina registra a leitura inicial ou a correção, com motivo de 3 caracteres ou mais. Nenhuma leitura se apaga.',
    origem: 'Operação · leituras de medidor. Muda o vencimento das preventivas da máquina.',
    criterios: [
      'No app, leitura menor que a anterior é recusada, dizendo a anterior.',
      'Sem sinal, a leitura espera na fila e sai sozinha.',
      'Na mesa, a correção sem motivo é recusada.',
    ],
    situacao: 'rascunho', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-EQP-09', titulo: 'Ler o adesivo da máquina', papel: 'operador',
    quero: 'apontar a câmera para o QR colado na máquina', para: 'lançar na máquina certa sem procurá-la numa lista de iguais',
    significa: 'O adesivo leva o código da máquina (BAS-001), nunca um endereço; o adesivo antigo, com o identificador, continua lendo (decisão 64). Lido, ele abre o que aquele perfil faz com ela: abastecer, apontar turno, abrir O.S. ou checklist. A tela mostra o código grande antes de seguir — o QR zera o erro de escolher a máquina errada na lista, mas não o adesivo colado na máquina errada.',
    calcula: 'A leitura resolve a máquina no próprio aparelho: o código, na frota que ele já tem, sem diferença de caixa; o adesivo antigo, na frota e depois no pacote do campo. Código que nenhuma máquina tem diz isso e o que fazer. Fora do pacote, a frase diz que a máquina não está neste aparelho — e não "não encontrada". O leitor usa o detector do navegador quando há e a biblioteca embutida quando não há, porque na obra não há internet para baixar nada.',
    origem: 'Frota · o adesivo impresso pelo arquivo de Equipamentos › Identificação. Nenhuma permissão nova: a leitura abre o que o perfil já podia fazer.',
    criterios: [
      'QR de outro sistema avisa e a leitura continua, sem fechar o leitor.',
      'Máquina fora do pacote do aparelho diz isso, com o caminho de saída.',
      'Sem câmera ao vivo, a foto do adesivo é lida do mesmo jeito.',
    ],
    situacao: 'rascunho', responsavel: 'Administrador', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-EQP-06', titulo: 'Alocar equipamento', papel: 'gestor operacional',
    quero: 'levar a máquina para outra obra', para: 'o custo cair no contrato certo desde o primeiro dia',
    significa: 'Leva a máquina para uma obra, com cliente, contrato e centro de custo opcionais, a partir de agora. A alocação anterior fecha no mesmo instante.',
    calcula: 'Fecha a alocação aberta e abre a nova na mesma transação. Obra é obrigatória. O banco não deixa a mesma máquina em dois lugares no mesmo período. Antes de gravar, a tela mostra o que a obra e o contrato exigem e falta, sem barrar.',
    origem: 'Equipamentos · alocações com vigência. O período anterior continua apropriando os custos da obra antiga.',
    criterios: [
      'Sem obra escolhida, o botão fica desabilitado.',
      'A alocação anterior aparece no Histórico de alocações com o fim preenchido.',
      'Obra e Alocada desde mudam no painel da máquina.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-EQP-07', titulo: 'Perguntar ao manual', papel: 'oficina e mecânicos',
    quero: 'perguntar aos manuais desta máquina', para: 'decidir o reparo com a página certa na mão',
    significa: 'Pergunta à IA sobre esta máquina. A resposta cita o documento e a página da biblioteca.',
    calcula: 'No app, abre o assistente já com a máquina escolhida. Na mesa, o prontuário tem o campo "Perguntar aos documentos desta máquina". A resposta sai dos documentos validados que alcançam a máquina.',
    origem: 'Biblioteca técnica · documentos validados; o histórico de perguntas fica com a máquina.',
    criterios: [
      'Toda resposta mostra o documento e a página de onde saiu.',
      'Sem permissão de perguntar à IA, a ação não aparece na ficha.',
      'As perguntas anteriores da máquina ficam abertas no prontuário da mesa.',
    ],
    situacao: 'rascunho', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },
  {
    codigo: 'NUM-EQP-08', titulo: 'Custo/h', papel: 'controladoria',
    quero: 'ver o custo por hora de cada máquina', para: 'achar a máquina cara antes que ela pese no contrato',
    significa: 'Custo direto da máquina nos últimos 30 dias dividido pelas horas de produção apontadas.',
    calcula: 'Custo direto dos últimos 30 dias ÷ horas de produção da máquina no mesmo período, em R$/h. Sem hora de produção, não há conta. A segunda linha diz se o custo é parcial, por lançamento sem preço.',
    origem: 'Custos · custo direto por máquina; Operação · horas de produção dos apontamentos. Coluna da lista da frota.',
    criterios: [
      'Sem hora apontada, a célula diz "Sem hora apontada", e não R$ 0,00.',
      'Sem permissão de ler custos, a célula diz "Sem acesso a custos".',
      'Com lançamento sem preço, a segunda linha diz "parcial, 30 dias".',
    ],
    situacao: 'rascunho', responsavel: 'Controladoria', revisadaEm: REVISAO,
  },
  {
    codigo: 'ABA-EQP-09', titulo: 'Histórico', papel: 'gestor operacional',
    quero: 'ver o que mudou na máquina e quem mudou', para: 'responder pela ficha sem depender da memória de alguém',
    significa: 'A trilha da máquina: cada alteração, com quem mudou, quando e o motivo. As alocações e situações anteriores ficam também nas abas Alocação e Situação.',
    calcula: 'Os eventos da trilha de auditoria da máquina, do mais recente ao mais antigo.',
    origem: 'Trilha de auditoria · alterações da ficha, da alocação e da situação. Preenche-se sozinha a cada gravação.',
    criterios: [
      'Cada linha diz a ação, o autor, o motivo e a data.',
      'Alterar a ficha cria uma linha nova no topo.',
      'Máquina sem alteração mostra "Nenhuma alteração desta máquina na trilha".',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },

  /* ------------------------------------------- CMB · Combustível (p.53) */
  {
    codigo: 'BTN-CMB-01', titulo: 'Lançar abastecimento', papel: 'operador',
    quero: 'lançar o abastecimento da máquina', para: 'o litro sair do tanque certo e o consumo ser calculado',
    significa: 'Registra a saída de combustível para uma máquina, de um tanque ou de um posto. No app, só do tanque ou do comboio.',
    calcula: 'Interno: baixa os litros do tanque escolhido, e o litro sai ao preço do último recebimento daquele tanque. Externo: pede posto, preço do litro e comprovante. A leitura do medidor entra antes; se ela for recusada, o lançamento inteiro cai. Com ordem, a ordem fecha quando o autorizado se esgota.',
    origem: 'Combustível · abastecimentos e lançamentos do tanque; Operação · leituras de medidor.',
    criterios: [
      'No app, o botão repete o volume antes de enviar, como "Lançar 320 L".',
      'No app, o "Desfazer" de 5 s tira o lançamento da fila; sem sinal, ele sai quando o sinal volta.',
      'Lançamento interno com preço é recusado: o preço vem do tanque.',
      'Externo sem comprovante é recusado, pedindo o número do cupom.',
    ],
    situacao: 'em_revisao', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'CMP-CMB-02', titulo: 'Litros', papel: 'operador',
    quero: 'informar quantos litros entraram', para: 'o volume do tanque continuar certo',
    significa: 'Volume abastecido. Sai do volume do tanque escolhido.',
    calcula: 'Número em L, maior que zero. No interno vira saída do tanque escolhido; no externo, não mexe em tanque.',
    origem: 'Combustível · abastecimentos e lançamentos do tanque.',
    criterios: [
      'No app, litros acima do volume do tanque mostram o volume dele e pedem conferência.',
      'Na mesa, o volume do tanque e o preço do litro aparecem ao escolher o tanque.',
      'Vazio ou zero é recusado com "Informe quantos litros entraram."',
    ],
    situacao: 'rascunho', responsavel: 'Almoxarifado', revisadaEm: REVISAO,
  },
  {
    codigo: 'CMP-CMB-03', titulo: 'Horímetro', papel: 'operador',
    quero: 'informar o horímetro', para: 'o consumo por hora sair certo',
    significa: 'Leitura do motor no abastecimento. É a base do consumo por hora e não pode ser menor que a anterior.',
    calcula: 'O valor vira leitura do medidor na hora do abastecimento, gravada antes dele. O consumo usa a diferença entre esta leitura e a do abastecimento anterior do mesmo produto.',
    origem: 'Operação · leituras de medidor, ligadas ao abastecimento.',
    criterios: [
      'Leitura menor que a anterior é recusada, dizendo a anterior.',
      'No app, a última leitura e quando foi aparecem abaixo do campo.',
      'Caminhão pede km; máquina pesada pede h.',
      'Sem leitura, o abastecimento entra, e o consumo dele fica sem cálculo.',
    ],
    situacao: 'em_revisao', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'NUM-CMB-04', titulo: 'Volume', papel: 'almoxarifado',
    quero: 'ver quanto combustível o sistema calcula em cada tanque', para: 'não deixar a obra sem diesel nem receber acima da capacidade',
    significa: 'O que o sistema calcula no tanque: recebimentos e chegadas de transferência menos abastecimentos e saídas de transferência, com os balanços e correções já aprovados.',
    calcula: 'Soma dos lançamentos do tanque, com sinal, em L. Pedido de balanço ou de correção só muda o volume depois de aprovado por quem tem alçada de ajuste.',
    origem: 'Combustível · lançamentos do tanque. Muda a cada lançamento.',
    criterios: [
      'Abaixo do volume vem a capacidade, como "de 15.000 L de capacidade".',
      'Volume acima da capacidade conta no card Acima da capacidade.',
      'A linha de total soma o volume dos tanques da lista.',
    ],
    situacao: 'rascunho', responsavel: 'Almoxarifado', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-CMB-05', titulo: 'Pedir balanço', papel: 'almoxarifado',
    quero: 'pedir o acerto do tanque pela medição física', para: 'o volume do sistema bater com o que está no tanque',
    significa: 'Pede a medição física do tanque, para acertar o volume calculado com o real.',
    calcula: 'Pede o volume contado, a data e a hora da conferência e o que ela achou, com 15 caracteres ou mais. Vira pedido de ajuste aguardando aprovação; quem tem alçada de ajuste aprova ou recusa, e só a aprovação acerta o volume.',
    origem: 'Combustível · pedidos de ajuste do tanque e alçada de ajuste da empresa.',
    criterios: [
      'O pedido soma 1 no card Ajustes para aprovar.',
      'O volume do tanque não muda até a aprovação.',
      'Motivo com menos de 15 caracteres é recusado, dizendo o mínimo.',
    ],
    situacao: 'rascunho', responsavel: 'Almoxarifado', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-CMB-06', titulo: 'Transferir para outro tanque', papel: 'almoxarifado',
    quero: 'mover combustível de um tanque para outro', para: 'abastecer o comboio sem perder o rastro do litro',
    significa: 'Move volume entre tanques do mesmo produto. Até o recebimento, a transferência fica em trânsito.',
    calcula: 'A saída entra nos lançamentos do tanque de origem e a transferência fica em trânsito. Na chegada, quem recebe informa quanto entrou; a diferença é a perda no transporte, com justificativa. Marcado "Chegou junto", saída e chegada entram no mesmo instante.',
    origem: 'Combustível · transferências e lançamentos dos dois tanques.',
    criterios: [
      'Só aparece como destino tanque do mesmo produto.',
      'O litro em trânsito aparece no card Em trânsito e não está em tanque nenhum.',
      'Chegada menor que a saída pede justificativa de 10 caracteres ou mais.',
    ],
    situacao: 'rascunho', responsavel: 'Almoxarifado', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-CMB-07', titulo: 'Autorizar abastecimento', papel: 'gestor operacional',
    quero: 'autorizar o abastecimento antes de ele acontecer', para: 'separar o que foi autorizado do que foi entregue',
    significa: 'Cria a ordem que libera o abastecimento de uma máquina antes de ele acontecer.',
    calcula: 'Cria a ordem com número próprio: máquina, tanque, litros autorizados e, se quiser, validade, obra e centro de custo. O abastecimento lançado contra a ordem desconta do autorizado; ela passa a atendida quando o saldo autorizado chega a zero.',
    origem: 'Combustível · ordens de abastecimento e o atendimento de cada uma.',
    criterios: [
      'A ordem nova aparece na lista com o número.',
      'O abastecimento ligado à ordem desconta do saldo autorizado.',
      'Cancelar a ordem pede o motivo.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'NUM-CMB-08', titulo: 'Consumo médio', papel: 'gestor operacional',
    quero: 'ver o consumo de cada máquina contra a faixa esperada', para: 'achar desperdício ou defeito antes do fechamento',
    significa: 'Litros entre dois abastecimentos divididos pelas horas de motor, contra a faixa esperada. Caminhão de odômetro mede em L/km.',
    calcula: 'Por abastecimento: litros do intervalo ÷ (leitura atual − leitura anterior), do mesmo produto, em L/h ou L/km. Na coluna, litros com base ÷ horas com base. Sem leitura nesta ou na anterior, ou com o medidor parado, o consumo fica sem cálculo, e não zero.',
    origem: 'Combustível · consumo por abastecimento, julgado no servidor contra a faixa da máquina ou, sem ela, a da configuração.',
    criterios: [
      'Consumo fora da faixa sai em vermelho, e a coluna Faixa esperada diz de que lado saiu.',
      'A segunda linha diz quantos lançamentos entraram na conta, como "8 de 10 lançamentos na conta".',
      'Sem base, a célula diz "Sem base de comparação".',
      'ARLA não é julgado contra a faixa do diesel.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-CMB-09', titulo: 'Lançar cobrança', papel: 'controladoria',
    quero: 'lançar a fatura do posto', para: 'conferir o cobrado contra o que foi abastecido',
    significa: 'Registra a cobrança do posto para conciliar com os abastecimentos externos.',
    calcula: 'Grava a fatura com fornecedor, posto opcional, documento, valor cobrado e competência; ela nasce aberta. Ao ligar os abastecimentos externos a ela, o servidor compara o cobrado com o lançado: diferença zero vira conciliada; qualquer outra, divergente.',
    origem: 'Combustível · cobranças dos postos e abastecimentos externos.',
    criterios: [
      'A cobrança nova aparece em Cobranças dos postos, aberta.',
      'Quem concilia não escolhe a situação: ela sai da diferença.',
      'Abastecimento já ligado a outra cobrança não é religado.',
      'Cobrança divergente conta nas Exceções de combustível do painel.',
    ],
    situacao: 'rascunho', responsavel: 'Controladoria', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-CMB-10', titulo: 'Escanear folha', papel: 'operador',
    quero: 'fotografar a folha de campo preenchida', para: 'a IA ler e eu só conferir',
    significa: 'Abre a câmera para a folha impressa de abastecimento ou de turno.',
    calcula: 'Escolhe a folha pelo código do topo e o instrumento do preenchimento. A foto vai para a leitura da IA no servidor, que marca onde teve dúvida; a conferência linha a linha vem em seguida. Precisa de sinal: não entra na fila do aparelho.',
    origem: 'Combustível e Operação · folhas de campo de abastecimento e de turno.',
    criterios: [
      'O código lido diferente da folha escolhida gera aviso, dizendo o que fazer.',
      'Só aparecem os tipos de folha que o perfil pode lançar.',
      'Na mesa, a mesma folha se escaneia pela tela de folhas do combustível.',
    ],
    situacao: 'em_revisao', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
];
